import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { File, HelpCircle, Upload, X, XCircle } from 'lucide-react';
import { useState } from 'react';

interface RejectPackageModalProps {
  open?: boolean; // Support both isOpen and open
  isOpen?: boolean; // Legacy support
  onClose: () => void;
  onConfirm?: (comments: string, file?: File) => void; // Legacy support
  onSubmit?: (comments: string, files?: File[]) => void; // New interface
  packageRef?: string;
  jobTitle?: string;
  targetName?: string; // New: generic target name
  showFileUpload?: boolean;
  mode?: 'reject' | 'request-info';
  isSubmitting?: boolean;
}

export function RejectPackageModal({
  open,
  isOpen,
  onClose,
  onConfirm,
  onSubmit,
  packageRef,
  jobTitle,
  targetName,
  showFileUpload = true, // Default to true now
  mode = 'reject',
  isSubmitting = false,
}: RejectPackageModalProps) {
  const [comments, setComments] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const isModalOpen = open ?? isOpen ?? false;

  if (!isModalOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (comments.trim()) {
      // Support both callback interfaces
      if (onSubmit) {
        onSubmit(comments, selectedFiles.length > 0 ? selectedFiles : undefined);
      } else if (onConfirm) {
        onConfirm(comments, selectedFiles[0]);
        setComments('');
        setSelectedFiles([]);
        onClose();
      }
    }
  };

  const handleCancel = () => {
    setComments('');
    setSelectedFiles([]);
    onClose();
  };

  const isRejectMode = mode === 'reject';

  const title = isRejectMode
    ? targetName
      ? `Reject ${targetName}`
      : jobTitle
        ? 'Reject Job'
        : packageRef
          ? `Reject Package ${packageRef}`
          : 'Reject Package'
    : targetName
      ? `Request Info for ${targetName}`
      : jobTitle
        ? 'Request More Information'
        : packageRef
          ? `Request Info for Package ${packageRef}`
          : 'Request More Information';

  const subtitle = isRejectMode
    ? 'Please provide a reason for rejection'
    : 'Please specify what additional information is required';

  const placeholder = isRejectMode
    ? 'Please provide detailed reasons for rejecting this job...'
    : 'Please describe what additional information or clarification is needed...';

  const buttonLabel = isRejectMode ? 'Confirm Rejection' : 'Send Request';
  const buttonColor = isRejectMode
    ? 'bg-red-600 hover:bg-red-700'
    : 'bg-amber-600 hover:bg-amber-700';
  const iconBg = isRejectMode ? 'bg-red-50 dark:bg-red-950' : 'bg-amber-50 dark:bg-amber-950';
  const iconColor = isRejectMode ? 'text-red-600' : 'text-amber-600';
  const Icon = isRejectMode ? XCircle : HelpCircle;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{title}</h2>
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleCancel} className="shrink-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Comments Field */}
          <div className="space-y-2">
            <Label htmlFor="rejection-comments" className="text-sm font-medium">
              {isRejectMode ? 'Rejection Comments' : 'Information Request'}{' '}
              <span className="text-red-600">*</span>
            </Label>
            <Textarea
              id="rejection-comments"
              placeholder={placeholder}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={6}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">{comments.length} / 500 characters</p>
          </div>

          {/* File Upload (only shown if enabled) */}
          {showFileUpload && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Supporting Documentation (Optional)</Label>

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-orange-600 bg-orange-50 dark:bg-orange-950/20'
                    : 'border-border hover:border-orange-600 hover:bg-muted/50'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <Upload className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      Drop files here or{' '}
                      <label className="text-orange-600 hover:text-orange-700 cursor-pointer underline">
                        browse
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleFileSelect}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                          multiple
                        />
                      </label>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PDF, Word, Excel, or images up to 10MB each
                    </p>
                  </div>
                </div>
              </div>

              {/* File List */}
              {selectedFiles.length > 0 && (
                <div className="space-y-2 mt-3">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="border border-border rounded-lg p-3 bg-muted/30">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center shrink-0">
                          <File className="w-4 h-4 text-orange-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveFile(index)}
                          className="shrink-0 h-8 w-8"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={handleCancel} className="border-border">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!comments.trim() || isSubmitting}
            className={buttonColor + ' text-white'}
          >
            <XCircle className="w-4 h-4 mr-2" />
            {isSubmitting ? 'Submitting...' : buttonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
